import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import { domain } from "../../../security";
import ReceivedItemModal from "./ReceivedItemModal";
import ViewManifestModal from "./ViewManifestModal";
import AuditInfoModal, { buildAuditRows } from "../../shared/AuditInfoModal"; // ✅ Added
import Pagination from "../Pagination";
import Loader from "../../loader/Loader";
import { FaSearch } from "react-icons/fa";
import { Clock } from "lucide-react"; // ✅ Added
import { useSelector } from "react-redux";
import { selectUserID } from "../../../redux/IchthusSlice";

const TransferHistoryTable = ({ refreshTrigger, onReceiveSuccess }) => {
  const currentUserID = useSelector(selectUserID); // ✅ Get ID for Cancel/Soft Delete

  const [transfers, setTransfers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(5);
  const [auditItem, setAuditItem] = useState(null); // ✅ Audit State

  const [receiveModalOpen, setReceiveModalOpen] = useState(false);
  const [viewItemsModalOpen, setViewItemsModalOpen] = useState(false);
  const [selectedTransfer, setSelectedTransfer] = useState(null);

  const fetchTransfers = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // ✅ Now fetching from API which has audit info (CreatedByFullName)
      const response = await axios.get(`${domain}/api/Transfers`);
      const activeTransfers = response.data.filter(
        (t) => t.status === "In Transit",
      );
      setTransfers(activeTransfers);
      setCurrentPage(1);
    } catch (err) {
      console.error(err);
      setError(err);
      toast.error("Failed to fetch transfers.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTransfers();
  }, [refreshTrigger, fetchTransfers]);

  // ✅ FIXED: Cancel Logic (Soft Delete)
  const handleRevert = async (id) => {
    if (
      !window.confirm(
        `Are you sure you want to cancel Transfer #${id}? Items will return to stock.`,
      )
    )
      return;

    try {
      // Use PUT soft-delete with UserID
      await axios.put(
        `${domain}/api/Transfers/soft-delete/${id}`,
        { userID: currentUserID },
        { headers: { "Content-Type": "application/json" } },
      );

      toast.success(`Transfer #${id} cancelled successfully.`);
      fetchTransfers();
    } catch (err) {
      const msg = err.response?.data?.message || "Failed to cancel transfer.";
      toast.error(msg);
    }
  };

  // Filtering
  const filteredTransfers = transfers.filter((t) => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      t.id.toString().includes(query) ||
      t.fromLocationName.toLowerCase().includes(query) ||
      t.toLocationName.toLowerCase().includes(query)
    );
  });

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentTransfers = filteredTransfers.slice(
    indexOfFirstItem,
    indexOfLastItem,
  );
  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString("en-US", {
      month: "short",
      day: "2-digit",
      year: "numeric",
    });
  };

  // Modals
  const openReceiveModal = async (t) => {
    try {
      const res = await axios.get(`${domain}/api/Transfers/${t.id}`);
      const fullData = res.data;
      const processedItems = fullData.items.map((item) => ({
        ...item,
        serialNumbers:
          item.serialNumberIds?.map((id) => ({
            id,
            serialName: `Ref #${id}`,
          })) || [],
      }));
      setSelectedTransfer({ ...fullData, items: processedItems });
      setReceiveModalOpen(true);
    } catch (e) {
      toast.error("Could not load details.");
    }
  };

  const openViewItemsModal = (t) => {
    setSelectedTransfer(t);
    setViewItemsModalOpen(true);
  };

  const handleConfirmReceive = async (t, dto) => {
    try {
      await axios.post(`${domain}/api/ReceivedTransfers`, dto);
      toast.success(`Transfer #${t.id} received.`);
      setReceiveModalOpen(false);
      onReceiveSuccess();
      fetchTransfers();
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to receive.");
    }
  };

  if (loading)
    return (
      <div className="p-8 flex justify-center">
        <Loader />
      </div>
    );
  if (error)
    return (
      <div className="p-8 text-center text-red-500">Failed to load data.</div>
    );

  return (
    <div className="w-full">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-4">
        <h2 className="text-lg font-bold text-gray-800 flex items-center gap-2">
          In Transit{" "}
          <span className="bg-indigo-100 text-indigo-700 text-xs px-2 py-0.5 rounded-full">
            {filteredTransfers.length}
          </span>
        </h2>
        <div className="relative w-full md:w-64">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <FaSearch className="text-gray-400 text-xs" />
          </div>
          <input
            type="text"
            placeholder="Search..."
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value);
              setCurrentPage(1);
            }}
            className="w-full pl-9 pr-4 py-1.5 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-indigo-500 bg-white"
          />
        </div>
      </div>

      {/* Table */}
      {filteredTransfers.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 rounded-lg border border-gray-200 text-gray-500 text-sm">
          No transfers found.
        </div>
      ) : (
        <>
          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden shadow-sm">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-200 text-xs uppercase text-gray-500 font-semibold">
                  <th className="px-6 py-3">Reference</th>
                  <th className="px-6 py-3">Route</th>
                  <th className="px-6 py-3 text-center">Items</th>
                  <th className="px-6 py-3 text-center">Status</th>
                  <th className="px-6 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {currentTransfers.map((t) => (
                  <tr key={t.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <span className="block text-sm font-medium text-gray-900">
                          #{t.id}
                        </span>
                        {/* ✅ Audit Button */}
                        <button
                          onClick={() => setAuditItem(t)}
                          className="text-amber-500 hover:text-amber-700 bg-amber-50 p-1 rounded transition-colors"
                          title="View Audit"
                        >
                          <Clock size={14} />
                        </button>
                      </div>
                      <span className="block text-xs text-gray-500 mt-0.5">
                        {formatDate(t.transferredDate)}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex flex-col text-sm">
                        <span className="text-gray-500 text-xs">
                          From:{" "}
                          <span className="text-gray-700 font-medium">
                            {t.fromLocationName}
                          </span>
                        </span>
                        <span className="text-gray-500 text-xs mt-1">
                          To:{" "}
                          <span className="text-gray-900 font-bold">
                            {t.toLocationName}
                          </span>
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-center">
                      <button
                        onClick={() => openViewItemsModal(t)}
                        className="text-xs font-medium text-indigo-600 hover:text-indigo-800 underline underline-offset-2"
                      >
                        View {t.itemsCount} Items
                      </button>
                    </td>
                    <td className="px-6 py-4 text-center">
                      <span className="inline-block px-2 py-1 rounded-md text-xs font-medium bg-amber-50 text-amber-700 border border-amber-200">
                        In Transit
                      </span>
                    </td>
                    <td className="px-6 py-4 text-right">
                      <div className="flex justify-end items-center gap-3">
                        <button
                          onClick={() => handleRevert(t.id)}
                          className="text-xs text-gray-500 hover:text-red-600 transition-colors"
                        >
                          Cancel
                        </button>
                        <button
                          onClick={() => openReceiveModal(t)}
                          className="bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-semibold px-3 py-1.5 rounded transition-colors"
                        >
                          Receive
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="mt-4">
            <Pagination
              itemsPerPage={itemsPerPage}
              totalItems={filteredTransfers.length}
              currentPage={currentPage}
              paginate={paginate}
            />
          </div>
        </>
      )}

      {/* Modals */}
      <ViewManifestModal
        isOpen={viewItemsModalOpen}
        onClose={() => setViewItemsModalOpen(false)}
        items={selectedTransfer?.items || []}
        transferId={selectedTransfer?.id}
      />
      {receiveModalOpen && selectedTransfer && (
        <ReceivedItemModal
          transfer={selectedTransfer}
          onClose={() => setReceiveModalOpen(false)}
          onConfirmReceive={handleConfirmReceive}
        />
      )}

      {/* ✅ Audit Modal */}
      <AuditInfoModal
        isOpen={!!auditItem}
        onClose={() => setAuditItem(null)}
        title={`Transfer #${auditItem?.id}`}
        rows={buildAuditRows(auditItem)}
      />
    </div>
  );
};

export default TransferHistoryTable;
